import clsx from 'clsx';
import type { Role } from '../../lib/api';

/**
 * Tailwind classes describing the pill background + text colour for each
 * portal role. Kept inline (not pulled from a shared map) because each role
 * uses a different combination of the global design tokens.
 */
const STYLES: Record<Role, string> = {
  super_admin: 'bg-ink-900 text-parchment-50 border-ink-900',
  academic_admin: 'bg-teal-700 text-white border-teal-700',
  finance_admin: 'bg-gold-700 text-white border-gold-700',
  nalim: 'bg-parchment-300 text-ink-900 border-parchment-300',
  teacher:
    'bg-[color:var(--color-success)]/15 text-[color:var(--color-success)] border-[color:var(--color-success)]/30',
  parent:
    'bg-[color:var(--color-warning)]/15 text-[color:var(--color-warning)] border-[color:var(--color-warning)]/30',
  student: 'bg-teal-300 text-ink-900 border-teal-300',
  support_viewer:
    'bg-ink-100 text-ink-700 border-ink-200 dark:bg-ink-700/40 dark:text-parchment-200 dark:border-ink-700/40',
  // Added in Phase 4 for the Hiring module. Re-uses the neutral parchment
  // background so the badge stays visually grouped with `nalim`.
  candidate:
    'bg-parchment-200 text-ink-900 border-parchment-300 dark:bg-ink-700/40 dark:text-parchment-200 dark:border-ink-700/40',
  hr: 'bg-teal-700 text-white border-teal-700',
};

export const formatRoleLabel = (role: Role): string =>
  role
    .split('_')
    .map((part) => (part.length === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)))
    .join(' ');

interface RoleBadgeProps {
  role: Role;
  className?: string | undefined;
}

export const RoleBadge = ({ role, className }: RoleBadgeProps) => (
  <span
    className={clsx(
      'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
      STYLES[role],
      className,
    )}
  >
    {formatRoleLabel(role)}
  </span>
);
